export interface NavItem {
  key: string;
  label: string;
  icon: string;
  to: string;
  /** Other paths that light this item up (tabs that live on their own route). */
  also?: string[];
  /** Visible/reachable only if the business has at least one of these modules installed. Omit for always-on items. */
  moduleGate?: string[];
  permission?: import("../../lib/permissions").OwnerPermission;
}

export interface NavSection {
  section: string | null;
  icon?: string;
  items: NavItem[];
}

/** The canvas sidebar: the daily pages on top, then groups named after the job (not the module) that open on the page you're in.
 * `moduleGate` also gates by platform — a service business never has 'restaurant'/'inventory'. */
export const navSections: NavSection[] = [
  {
    section: null,
    items: [
      { key: "Overview", label: "Overview", icon: "dashboard", to: "/", permission: "View reports" },
      { key: "Sales", label: "Reports", icon: "trending_up", to: "/sales", also: ["/analytics"], moduleGate: ["reports"], permission: "View reports" },
      { key: "Bills", label: "Bills", icon: "receipt_long", to: "/bills", moduleGate: ["pos", "service"], permission: "View reports" },
      { key: "Appointments", label: "Appointments", icon: "event", to: "/appointments", moduleGate: ["service"], permission: "Operations & menu" },
    ],
  },
  {
    section: "Catalog",
    icon: "menu_book",
    items: [
      { key: "Menu", label: "Menu", icon: "menu_book", to: "/menu", moduleGate: ["restaurant"], permission: "Operations & menu" },
      { key: "Products", label: "Products", icon: "inventory_2", to: "/inventory", moduleGate: ["inventory"], permission: "Inventory & staff" },
      { key: "Services", label: "Services", icon: "design_services", to: "/services", moduleGate: ["catalog"], permission: "Operations & menu" },
      { key: "Ingredients", label: "Ingredients", icon: "egg", to: "/ingredients", moduleGate: ["restaurant"], permission: "Inventory & staff" },
      { key: "Purchasing", label: "Purchasing", icon: "local_shipping", to: "/purchasing", moduleGate: ["inventory"], permission: "Inventory & staff" },
    ],
  },
  {
    section: "Customers",
    icon: "loyalty",
    items: [
      { key: "Loyalty", label: "Customers", icon: "groups", to: "/customers", moduleGate: ["crm"], permission: "Operations & menu" },
      { key: "SvcClients", label: "Clients", icon: "contacts", to: "/clients", moduleGate: ["service"], permission: "Operations & menu" },
      { key: "Marketing", label: "Promotions", icon: "sell", to: "/marketing", moduleGate: ["crm"], permission: "Operations & menu" },
    ],
  },
  {
    section: "Team",
    icon: "badge",
    items: [
      { key: "Team", label: "Team", icon: "group", to: "/staff", permission: "Inventory & staff" },
      { key: "Payouts", label: "Payroll", icon: "payments", to: "/payroll", permission: "Payroll" },
    ],
  },
  {
    // Multi-store access is limited by the plan's max_stores (enforced server-side), not a module.
    section: "Stores",
    icon: "storefront",
    items: [
      { key: "Stores", label: "Stores", icon: "storefront", to: "/stores", permission: "View reports" },
      { key: "Tables", label: "Floor & tables", icon: "table_restaurant", to: "/tables", moduleGate: ["restaurant"], permission: "Operations & menu" },
      { key: "Devices", label: "Devices", icon: "devices", to: "/devices", permission: "Operations & menu" },
      { key: "PrintHubs", label: "Print hubs", icon: "print", to: "/print-hubs", permission: "Operations & menu" },
    ],
  },
  {
    section: "Business",
    icon: "domain",
    items: [
      { key: "Modules", label: "Modules", icon: "widgets", to: "/modules", permission: "Billing & plan" },
      { key: "GuestPortal", label: "Guest Portal", icon: "language", to: "/guest-portal", permission: "Operations & menu" },
      { key: "Payments", label: "Payment methods", icon: "credit_card", to: "/payments", permission: "Operations & menu" },
      { key: "Integrations", label: "Integrations", icon: "hub", to: "/integrations", permission: "Billing & plan" },
      { key: "Billing", label: "Billing & plan", icon: "workspace_premium", to: "/billing", permission: "Billing & plan" },
      { key: "Settings", label: "Settings", icon: "tune", to: "/settings", permission: "Operations & menu" },
    ],
  },
];

// A page under an item (a store's page under Stores) lights and is gated by that item.
export const isNavItemActive = (item: NavItem, pathname: string) =>
  item.to === pathname || !!item.also?.includes(pathname) || (item.to !== "/" && pathname.startsWith(item.to + "/"));

/** True if the business's installed modules satisfy this nav item's gate (always true when ungated). */
export function isNavItemUnlocked(item: NavItem, installedModules: ReadonlySet<string>): boolean {
  if (!item.moduleGate || item.moduleGate.length === 0) return true;
  return item.moduleGate.some((m) => installedModules.has(m));
}

/** A route's nav entry — used to gate direct URL access by module and permission, not just hide the link. */
export function findNavItem(pathname: string): NavItem | undefined {
  for (const sec of navSections) {
    const found = sec.items.find((i) => isNavItemActive(i, pathname));
    if (found) return found;
  }
  return undefined;
}

// Every page's title/subtitle, rendered once by AppShell's header. Routes don't render their own.
export const pageMeta: Record<string, { title: string; sub: string }> = {
  "/settings/receipt": { title: "Receipt design", sub: "" },
  "/": { title: "Overview", sub: "Today across your business" },
  "/sales": { title: "Reports", sub: "Sales for this store" },
  "/analytics": { title: "Reports", sub: "Across every store" },
  "/bills": { title: "Bills", sub: "Every transaction and receipt" },
  "/appointments": { title: "Appointments", sub: "Bookings across your service staff" },
  "/clients": { title: "Clients", sub: "People who book services, shared with Appointments" },
  "/services": { title: "Services", sub: "What clients can book, with price and length" },
  "/inventory": { title: "Products", sub: "Catalog, stock and transfers" },
  "/purchasing": { title: "Purchasing", sub: "Suppliers and what you've ordered" },
  "/customers": { title: "Customers", sub: "Members, points and the loyalty program" },
  "/marketing": { title: "Promotions", sub: "Discounts that apply on the register at checkout" },
  "/stores": { title: "Stores", sub: "How each location is doing this month" },
  "/tables": { title: "Floor & tables", sub: "Tables and reservations, live on the register" },
  "/menu": { title: "Menu", sub: "What the register and Guest Portal sell" },
  "/ingredients": { title: "Ingredients", sub: "Stock for recipes, used when items sell" },
  "/staff": { title: "Team", sub: "Staff, shifts, performance and access" },
  "/payroll": { title: "Payroll", sub: "Payouts and payroll history" },
  "/settings": { title: "Settings", sub: "Your business and your account" },
  "/account": { title: "Settings", sub: "Your business and your account" },
  "/devices": { title: "Devices", sub: "Registers, kitchen screens and terminals" },
  "/print-hubs": { title: "Print hubs", sub: "A PC in the store that drives your kitchen printers" },
  "/modules": { title: "Modules", sub: "Switch features on per business, pay for what you use" },
  "/billing": { title: "Billing & plan", sub: "Your subscription and payment history" },
  "/payments": { title: "Payment methods", sub: "What cashiers can pick at checkout" },
  "/guest-portal": { title: "Guest Portal", sub: "Where guests find this store, order and keep their member card" },
  "/integrations": { title: "Integrations", sub: "Send your data to the tools you already use" },
  "/more": { title: "More", sub: "" },
};
